/**
 * Oppgaver (#293): work given to one accountable person, with or without a shift
 * (backend/controllers/admin/taskController.js). Read with the schedule, written with schedule.manage.
 * A write names the revision it read; a 409 task_changed carries the current task in `current`.
 */
import { del, get, post, put } from '@/api/client';
import { useCan } from '@/auth/caps';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type Requirement = 'comment' | 'measurement';
export type TaskStatus = 'open' | 'done' | 'exception';

export interface ApiTask {
  id: number;
  title: string;
  type_id: number | null;
  type: { name: string; icon: string | null; color: string | null } | null;
  start_date: string;
  due_date: string;
  /** HH:MM; null is untimed, never 00:00 */
  start_time: string | null;
  /** planning effort in minutes; never registered or paid time */
  estimate_minutes: number | null;
  requirement: Requirement | null;
  assignee_id: number | null;
  assignee_name: string | null;
  /** the planned shift the task was given from: provenance only */
  shift_id: number | null;
  shift: { start: string; end: string; type_name: string | null } | null;
  series_id: number | null;
  occurrence_date: string | null;
  status: TaskStatus;
  revision: number;
  done_by_name: string | null;
  done_at: string | null;
  exception_reason: string | null;
  comments: number;
}

export interface TaskEvent {
  id: number;
  kind:
    | 'created'
    | 'edited'
    | 'comment'
    | 'followup'
    | 'complete'
    | 'exception'
    | 'reopen'
    | 'deleted'
    | 'restored';
  at: string;
  actor_name: string | null;
  requirement: Requirement | null;
  comment: string | null;
  measurement: number | null;
  reason: string | null;
}

export interface TaskWrite {
  title: string;
  type_id: number | null;
  start_date: string;
  due_date: string;
  start_time: string | null;
  estimate_minutes: number | null;
  requirement: Requirement | null;
  assignee_id: number | null;
  shift_id: number | null;
}
export interface TaskCreate extends TaskWrite {
  client_key: string;
  save_as_type?: { icon: string | null; color: string | null };
  repeat?: { cadence: 'daily' | 'weekly' | 'fortnightly'; until: string };
}

export function useTasks(from: string, to: string) {
  const can = useCan();
  return useQuery({
    queryKey: ['tasks', from, to],
    queryFn: () => get<ApiTask[]>(`/admin/tasks?from=${from}&to=${to}`),
    enabled: can('vaktplan.se'),
    staleTime: 15_000,
  });
}

export function useTask(id: number | null) {
  return useQuery({
    queryKey: ['task', id],
    queryFn: () => get<ApiTask & { events: TaskEvent[] }>(`/admin/tasks/${id}`),
    enabled: id != null,
  });
}

export function useTaskWrites() {
  const qc = useQueryClient();
  const done = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['tasks'] }),
      qc.invalidateQueries({ queryKey: ['task'] }),
      qc.invalidateQueries({ queryKey: ['shift-task-library'] }),
    ]);
  return {
    create: useMutation({
      mutationFn: (body: TaskCreate) => post<{ task: ApiTask; created: number }>('/admin/tasks', body),
      onSettled: done,
    }),
    update: useMutation({
      mutationFn: ({
        id,
        body,
      }: { id: number; body: TaskWrite & { expected_revision: number; scope?: 'future' } }) =>
        put<ApiTask>(`/admin/tasks/${id}`, body),
      onSettled: done,
    }),
    complete: useMutation({
      mutationFn: ({
        id,
        body,
      }: {
        id: number;
        body: {
          expected_revision: number;
          comment?: string;
          measurement?: number;
          exception_reason?: string;
        };
      }) => post<ApiTask>(`/admin/tasks/${id}/complete`, body),
      onSettled: done,
    }),
    reopen: useMutation({
      mutationFn: ({ id, revision }: { id: number; revision: number }) =>
        post<ApiTask>(`/admin/tasks/${id}/reopen`, { expected_revision: revision }),
      onSettled: done,
    }),
    comment: useMutation({
      mutationFn: ({ id, text, follow_up }: { id: number; text: string; follow_up: boolean }) =>
        post<ApiTask>(`/admin/tasks/${id}/comments`, { text, follow_up }),
      onSettled: done,
    }),
    remove: useMutation({
      mutationFn: ({ id, revision, scope }: { id: number; revision: number; scope?: 'future' }) =>
        del<{ ids: number[]; undo_minutes: number }>(
          `/admin/tasks/${id}?revision=${revision}${scope ? `&scope=${scope}` : ''}`,
        ),
      onSettled: done,
    }),
    restore: useMutation({
      mutationFn: (id: number) => post<{ ids: number[] }>(`/admin/tasks/${id}/restore`, {}),
      onSettled: done,
    }),
  };
}
